import { NewPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import TeamStore from 'iam/TeamStore'
import NewTeamModalViewModel from './components/NewTeamModal/NewTeamModalViewModel'
import { GetCurrentMember } from './SessionStore'
import TeamMemberStore from './TeamMemberStore'
import { TeamListViewModel } from './screens/Teams/TeamList/TeamListViewModel'
import Workspace from './models/Workspace'
import TeamViewModel from './screens/Teams/Team/TeamViewModel'
import { observable, computed, action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Id, Router } from 'types/definitions'
import Team from './models/Team'
import link from 'infra/link'
import TeamRoleStore from './TeamRoleStore'
import InvitationStore from './InvitationStore'
import BillingStore from 'billing/BillingStore'
import { AlertFactory } from 'alerts/Alert'

export class TeamsController {
  /**
   * Team View Models for details by id
   */
  private teamVMs = new Map<string, TeamViewModel>()

  @observable public current: TeamViewModel | null = null

  /**
   * New team modal
   */
  newTeamVM = new NewTeamModalViewModel(
    this.teamMemberStore,
    this.teamStore,
    this.workspace,
  )

  /**
   * Team switcher
   */
  teamListVM = new TeamListViewModel(
    this.workspace,
    () => this.teams,
    () => (this.current ? this.current.team : Team.none),
  )

  constructor(
    private workspace: Workspace,
    private teamMemberStore: TeamMemberStore,
    private teamStore: TeamStore,
    private getCurrentMember: GetCurrentMember,
    private teamRoleStore: TeamRoleStore,
    private invitationStore: InvitationStore,
    private router: Router,
    private billingStore: BillingStore,
    private newPaymentMethod: NewPaymentMethodDelegate,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  @computed
  get teams() {
    return this.teamMemberStore
      .teamMembersForWorkspaceMember()
      .map((m) => m.team)
  }

  @computed
  get canCreateTeam() {
    return this.getCurrentMember().hasPermission('CREATE_TEAM')
  }

  @computed
  get canViewTeams() {
    return (
      this.canCreateTeam ||
      this.getCurrentMember().hasPermission('ADMIN_TEAMS_VIEW')
    )
  }

  @action.bound
  setCurrent(vm: TeamViewModel | null) {
    if (this.current) {
      this.current.deactivate()
    }
    this.current = vm
    if (vm) {
      vm.activate()
    }
  }

  viewTeam = task(async (slug: string | null | undefined) => {
    if (this.teams.length === 0) {
      return
    }

    if (!slug) {
      this.preselectTeam()
      return
    }

    const team = this.teams.find((t) => t.slug === slug)

    if (!team) {
      this.preselectTeam()
      return
    }

    const teamId = Id.assert(team.id)

    if (this.current && this.current.team.id === teamId) {
      return this.current
    }

    const existing = this.teamVMs.get(teamId)
    if (existing) {
      this.setCurrent(existing)
      return existing
    }

    const vm = this.createTeamViewModel(team)
    this.setCurrent(vm)
    this.teamVMs.set(teamId, vm)
    return vm
  })

  preselectTeam = () => {
    if (!this.current && this.teams.length > 0) {
      this.router.replace(link.team(this.teams[0].slug))
    } else {
      this.router.replace(link.team())
    }
  }

  /**
   * Activates the root controller.
   */
  activate = task(async () => {
    await this.teamMemberStore.fetchUserTeamMembersForWorkspace()
  })

  /**
   * Called when the view model is no longer actively being used.
   */
  deactivate() {
    this.setCurrent(null)
  }

  private createTeamViewModel(team: Team) {
    return new TeamViewModel(
      team,
      this.teamStore,
      this.getCurrentMember,
      this.teamRoleStore,
      this.teamMemberStore,
      this.invitationStore,
      this.billingStore,
      this.newPaymentMethod,
      this.newAlert,
    )
  }
}

export class TeamNotFoundError extends Error {}
